// FieldMapperComponent.tsx - Reusable field mapping component with fixed infinite loop
import React, { useEffect, useState, useRef } from 'react';
import { Select, Divider, Typography, Alert } from 'antd';
import { ArrowRightOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { findMatchingFields } from '../utils/csvUtils';
import logger from '../utils/logger';

const { Text, Title } = Typography;
const { Option } = Select;

// Field object type definition
export interface FieldDefinition {
  name: string;
  label?: string;
  required?: boolean;
  similarNames?: string[];
  type?: string;
  [key: string]: any;
}

interface FieldMapperProps {
  sourceFields: string[];
  targetFields: FieldDefinition[];
  onChange: (mappings: Record<string, string>) => void;
  initialMappings?: Record<string, string>;
}

const MapperContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const MappingRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
  gap: 0.75rem;
`;


const TargetField = styled.div`
  flex: 1;
  text-align: right;
  padding-right: 0.5rem;
`;

const ArrowContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8c8c8c;
`;
const NavigationBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #e8e8e8;
  z-index: 10;
`;

const SourceField = styled.div`
  flex: 1;
`;

const RequiredIndicator = styled.span`
  color: #ff4d4f;
  margin-left: 4px;
`;

/**
 * Reusable component for mapping source fields (e.g., CSV columns) to target fields (e.g., product fields)
 */
const FieldMapperComponent: React.FC<FieldMapperProps> = ({
  sourceFields,
  targetFields,
  onChange,
  initialMappings = {}
}) => {
  // Add "Don't import" option to source fields
  const allSourceFields = [...sourceFields, "Don't import"];
  
  // State for field mappings
  const [mappings, setMappings] = useState<Record<string, string>>(initialMappings);
  const [usedSourceFields, setUsedSourceFields] = useState<string[]>([]);
  
  // Use a ref to track if initial auto-mapping has been done
  const initialMappingDoneRef = useRef(false);

  // Effect to find automatic matches when source fields change - FIXED to prevent infinite loop
  useEffect(() => {
    // Only run auto-mapping if we have source fields AND 
    // either mappings is empty OR the initialMappingDoneRef is false
    if (sourceFields.length > 0 && 
        (Object.keys(mappings).length === 0 || !initialMappingDoneRef.current)) {
      
      // Set ref to true to prevent this from running again
      initialMappingDoneRef.current = true;
      
      // Only compute auto-mappings if we don't already have mappings
      if (Object.keys(mappings).length === 0) {
        logger.log('Computing auto-mappings for fields');
        const autoMappings = findMatchingFields(sourceFields, targetFields);
        
        // Update local state
        setMappings(autoMappings);
        
        // Update used fields
        const used = Object.values(autoMappings).filter(Boolean);
        setUsedSourceFields(used);
        
        // Notify parent
        onChange(autoMappings);
      }
    }
  }, [sourceFields, targetFields, onChange]); // Removed mappings from dependency array

  // Handle selection change
  const handleFieldChange = (targetFieldName: string, sourceFieldName: string) => {
    const newMappings = { ...mappings };
    
    // Remove previous mapping for this target field from usedSourceFields
    const previousMapping = mappings[targetFieldName];
    if (previousMapping && previousMapping !== "Don't import") {
      setUsedSourceFields(prev => prev.filter(field => field !== previousMapping));
    }
    
    // Add new mapping
    newMappings[targetFieldName] = sourceFieldName;
    
    // Update usedSourceFields if not "Don't import"
    if (sourceFieldName && sourceFieldName !== "Don't import") {
      setUsedSourceFields(prev => [...prev.filter(f => f !== sourceFieldName), sourceFieldName]);
    }
    
    setMappings(newMappings);
    onChange(newMappings);
  };

  // Check for missing required fields
  const missingRequiredFields = targetFields
    .filter(field => field.required && (!mappings[field.name] || mappings[field.name] === "Don't import"))
    .map(field => field.label || field.name);

  return (
    <MapperContainer>
      <Title level={4}>Map Fields</Title>
      
      {missingRequiredFields.length > 0 && (
        <Alert
          message="Missing Required Fields"
          description={
            <div>
              Please map the following required fields:
              <ul>
                {missingRequiredFields.map(field => (
                  <li key={field}>{field}</li>
                ))}
              </ul>
            </div>
          }
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: '1rem' }}
        />
      )}
      
      <div className="field-header-row" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <div style={{ flex: 1, fontWeight: 'bold' }}>System Field</div>
        <div style={{ width: '40px' }}></div>
        <div style={{ flex: 1, fontWeight: 'bold' }}>CSV Column</div>
      </div>
      
      <Divider style={{ margin: '0.5rem 0 1rem' }} />
      
      {targetFields.map((field) => (
        <MappingRow key={field.name}>
          <TargetField>
            <Text strong={field.required}>
              {field.label || field.name}
              {field.required && <RequiredIndicator>*</RequiredIndicator>}
            </Text>
          </TargetField>
          
          <ArrowContainer>
            <ArrowRightOutlined />
          </ArrowContainer>
          
          <SourceField>
            <Select
              style={{ width: '100%' }}
              value={mappings[field.name] || undefined}
              onChange={(value) => handleFieldChange(field.name, value)}
              placeholder="Select a CSV column"
              status={field.required && (!mappings[field.name] || mappings[field.name] === "Don't import") ? 'error' : ''}
            >
              {allSourceFields.map((sourceField) => {
                const isUsed = usedSourceFields.includes(sourceField) && 
                               mappings[field.name] !== sourceField &&
                               sourceField !== "Don't import";
                
                return (
                  <Option 
                    key={sourceField} 
                    value={sourceField} 
                    disabled={isUsed}
                  >
                    {sourceField} {isUsed && '(already mapped)'}
                  </Option>
                );
              })}
            </Select>
          </SourceField>
        </MappingRow>
      ))}
    </MapperContainer>
  );
};

export default FieldMapperComponent;